/**
 * Layout tokens for the Projects-route tables (projects list, briefs,
 * folders/approval, brief templates) so their Name columns end at the same
 * point and read as one consistent layout when tabbing between routes.
 *
 * THE BALANCING RULE (don't break this without re-reading it):
 *
 * The tables share a right edge; only the project sidebar shifts the LEFT start
 * (it lives inside the in-project routes). So if the "data region" — everything
 * to the right of Name — is a fixed width, then `Name-end = right-edge − region`
 * is identical for any tables that use the same region, and their Name columns
 * line up. Name is the leftover, so it's the widest column and grows where
 * there's more room — no per-table percentages.
 *
 * There are TWO groups, because the sidebar makes the in-project tables ~250px
 * narrower; forcing them to share the no-sidebar region would crush their Name
 * column. Each group's region = its HEAVIEST table's right block (all columns
 * already at content width), and the lighter tables in the group pad up to it
 * with a fixed-width SPACER column rather than stretching real columns:
 *
 *   • NO-SIDEBAR group → 47.5rem (760px), set by the PROJECTS table.
 *     Used by: projects list, brief templates.
 *   • SIDEBAR group → 38.25rem (612px). Originally set by the folders/approval
 *     table, which no longer exists, so the value is frozen rather than derived.
 *     Used by: delivery-job-grid.
 *
 * Therefore: if you change a data-column width or add/remove a column, re-balance
 * so each table's data columns (+ its spacer) sum to its group's region and the
 * Name token subtracts that same total. Verify with the second column's
 * `getBoundingClientRect()` — its left must match across tables in the SAME group.
 */

// Name column = all width left of the group's fixed data region (see the rule
// above). NO-SIDEBAR group: 47.5rem (760px), set by the projects table.
export const TABLE_NAME_COL_NO_SIDEBAR = 'w-[calc(100%_-_47.5rem)]';
// SIDEBAR group: 38.25rem (612px), frozen — see the rule above.
export const TABLE_NAME_COL_SIDEBAR = 'w-[calc(100%_-_38.25rem)]';

// Canonical data-column widths (content max), tuned so the projects table's
// right block fits the 43rem data region. Same key → same width everywhere.
export const TABLE_COL_WIDTH = {
  count: 'w-24', // 96px  — numeric counts (folders, subfolders, comments)
  type: 'w-40', // 160px — type badge
  person: 'w-50', // 200px — avatar + name (owner / created by), truncates
  lastActivity: 'w-35', // 140px — relative date (projects table only)
  dateText: 'w-28', // 112px — relative / short date (when NOT the trailing column)
  statusBadge: 'w-32', // 128px — draft / published badge
  actions: 'w-13', // 52px  — kebab (36px button + 8px padding each side)
} as const;
